import type { Ref } from 'vue';
import type {
  ApplicantCategoryEnum,
  TranscriptRequestStatusEnum,
} from '@/types/composition-functions';
import {
  useGetTranscriptRequestsLazyQuery,
  useGetTranscriptRequestQuery,
  useGetMyTranscriptRequestsLazyQuery,
  useGetGeneratedTranscriptQuery,
} from '@/types/vue-apollo-helpers';

export interface TranscriptRequestFilters {
  status?: TranscriptRequestStatusEnum | null;
  transcript_type_id?: string | null;
  applicant_category?: ApplicantCategoryEnum | null;
  search?: string | null;
  date_from?: string | null;
  date_to?: string | null;
  first?: number;
  page?: number;
}

export const useTranscriptRequests = (
  variables?: Ref<TranscriptRequestFilters> | (() => TranscriptRequestFilters),
  options: { autoFetch?: boolean } = {},
) => {
  const { result, loading, refetch, load, error } = useGetTranscriptRequestsLazyQuery(
    variables,
    { fetchPolicy: 'cache-and-network' },
  );

  if (options.autoFetch) load();

  const requests = computed(() => result.value?.transcriptRequests?.data ?? []);
  const paginatorInfo = computed(() => result.value?.transcriptRequests?.paginatorInfo ?? null);

  return { requests, paginatorInfo, loading, refetch, load, error };
};

export const useTranscriptRequest = (id: Ref<string | undefined>) => {
  const { result, loading, refetch, error } = useGetTranscriptRequestQuery(
    () => ({ id: id.value ?? '' }),
    () => ({ enabled: !!id.value, fetchPolicy: 'cache-and-network' }),
  );

  const request = computed(() => result.value?.transcriptRequest ?? null);

  return { request, loading, refetch, error };
};

export const useMyTranscriptRequests = (options: { autoFetch?: boolean } = {}) => {
  const { result, loading, refetch, load, error } = useGetMyTranscriptRequestsLazyQuery({
    fetchPolicy: 'cache-and-network',
  });

  if (options.autoFetch) load();

  const requests = computed(() => result.value?.myTranscriptRequests ?? []);

  return { requests, loading, refetch, load, error };
};

export const useGeneratedTranscript = (id: Ref<string | undefined>) => {
  const { result, loading, refetch, error } = useGetGeneratedTranscriptQuery(
    () => ({ id: id.value ?? '' }),
    () => ({ enabled: !!id.value, fetchPolicy: 'cache-and-network' }),
  );

  const transcript = computed(() => result.value?.generatedTranscript ?? null);

  return { transcript, loading, refetch, error };
};
